import React, { useState, useEffect } from 'react'
import { Global } from '@emotion/core'

import {
  designSystem,
  Box,
  Select,
  StackView,
  Text,
  ThemeProvider,
  utils,
  Tooltip,
} from '@planning-center/ui-kit'

const { colors, generateColorScale, flattenPalette } = designSystem
const apps = [
  'check-ins',
  'giving',
  'groups',
  'music-stand',
  'pco',
  'people',
  'planning-center',
  'projector',
  'registrations',
  'calendar',
  'services',
]

const colorModes = apps.reduce((collection, app) => {
  const appColor = colors[app]
  return {
    ...collection,
    [`[data-theme="${app}"]`]: utils.objectToCSSProperties('colors', {
      ...generateColorScale({
        key: 'primary',
        baseColor: appColor,
      }),
    }),
  }
}, {})

const darkModeStyles = {
  grey: [
    'hsl(200,4%,3%)', // 0. surfaceSecondary
    'hsl(200,4%,6%)', // 1. surfaceTertiary
    'hsl(200,4%,9%)', // 2. highlight
    'hsl(200,4%,12%)', // 3. highlightSecondary
    'hsl(200,4%,20%)', // 4. separatorTertiary
    'hsl(200,4%,22%)', // 5. separatorSecondary
    'hsl(200,4%,25%)', // 6. separator
    'hsl(200,4%,30%)', // 7.
    'hsl(200,4%,69%)', // 8. foregroundTertiary
    'hsl(200,4%,85%)', // 9. foregroundSecondary
  ],

  foreground: 'hsl(0,0%,100%)',
  foregroundSecondary: 'grey-9',
  foregroundTertiary: 'grey-8',

  background: 'hsl(200,4%,2%)',
  backgroundSecondary: 'grey-0',
  backgroundTertiary: 'grey-1',

  surface: 'grey-2',
  surfaceSecondary: 'grey-3',
  surfaceTertiary: 'grey-4',

  separator: 'hsl(200, 6%, 32%)',
  separatorSecondary: 'hsl(200, 6%, 24%)',
  separatorTertiary: 'hsl(200, 6%, 24%)',
  separatorHover: 'hsl(200, 6%, 32%)',
  separatorFocus: 'hsl(88, 46%, 36%)',
  separatorFocusSecondary: 'hsl(88, 72%, 20%)',

  highlight: 'grey-6',
  highlightSecondary: 'grey-7',

  linkForeground: 'hsl(197, 56%, 44%)',
  linkBackground: 'hsl(200,100%,98%)',
}

const globalStyles = {
  ...colorModes,
  '[data-dark-mode="true"]': {
    ...utils.objectToCSSProperties('colors', flattenPalette(darkModeStyles)),
    backgroundColor: 'var(--colors-background)',
    color: 'var(--colors-foreground)',
  },
}

function Dot({ color, ...restProps }) {
  return (
    <Box
      width={2}
      height={2}
      radius="circle"
      backgroundColor={color}
      {...restProps}
    />
  )
}

export function ThemeWrapper({ children }) {
  useEffect(() => {
    const darkMode =
      window.____THEME____ === 'dark'
        ? 'true'
        : window.localStorage.getItem('dark-mode')
    if (darkMode) {
      document.documentElement.dataset.darkMode = darkMode
    }
  }, [])
  return (
    <ThemeProvider>
      <Global styles={globalStyles} />
      {children}
    </ThemeProvider>
  )
}

export function AppThemePicker() {
  const [value, setValue] = useState(null)
  useEffect(() => {
    const theme = ThemeProvider.themeStorage.get()
    if (theme) {
      setValue(theme)
      document.documentElement.dataset.theme = theme
    }
  }, [])
  return (
    <Select.Inline
      light
      width={5}
      size="sm"
      tooltip={{ title: 'choose product theme' }}
      placement="bottom-end"
      emptyValue={<Dot color="grey-6" border="2px solid white" />}
      renderValue={([selected]) => (
        <Dot
          color={selected ? selected.value : 'grey-2'}
          border="2px solid white"
        />
      )}
      value={value}
      onChange={(event) => {
        ThemeProvider.themeStorage.set(event.value)
        setValue(event.value)
        document.documentElement.dataset.theme = event.value
      }}
    >
      {apps.map((app) => (
        <Select.Option key={app} value={app}>
          {(isSelected) => (
            <StackView axis="horizontal" alignment="center" spacing={1}>
              <Dot
                color={app}
                boxShadow={
                  isSelected && [
                    '0px 0px 0px 2px var(--colors-surface-background)',
                    '0px 0px 0px 4px var(--colors-foreground-tertiary)',
                  ]
                }
              />
              <Text>{app}</Text>
            </StackView>
          )}
        </Select.Option>
      ))}
    </Select.Inline>
  )
}

export function DarkModeToggle({ ...props }) {
  const [darkMode, setDarkMode] = useState(false)
  useEffect(() => {
    const darkMode =
      window.____THEME____ === 'dark'
        ? 'true'
        : window.localStorage.getItem('dark-mode')
    if (darkMode) {
      setDarkMode(darkMode === 'true' ? true : false)
    }
  }, [])
  return (
    <Tooltip title={`toggle dark mode ${darkMode ? 'off' : 'on'}`}>
      <StackView
        as="button"
        alignment="center"
        distribution="center"
        cursor="pointer"
        padding={0.5}
        radius={3}
        aria-label={`toggle dark mode ${darkMode ? 'off' : 'on'}`}
        hover={{ backgroundColor: 'light-0' }}
        onClick={() => {
          const nextDarkMode = !darkMode
          setDarkMode(nextDarkMode)
          window.localStorage.setItem(
            'dark-mode',
            nextDarkMode === true ? 'true' : 'false'
          )
          document.documentElement.dataset.darkMode = nextDarkMode
        }}
      >
        <Text
          width="18px"
          height="16px"
          fontSize="18px"
          lineHeight="18px"
          {...props}
        >
          {darkMode ? '☀️' : '🌝'}
        </Text>
      </StackView>
    </Tooltip>
  )
}
